
import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, ChatMessage, PollOption } from '../../types';
import { chatApi } from '../../services/apiService';
import { createCommunityRoomConnection, CommunityRoomConnection, ConnectionStatus } from '../../services/realtimeService';
import { Send, Users, Info, MessageSquareQuote, BarChart2, Plus, X, Check, ChevronLeft, CornerDownRight, Eye, Ghost, Loader2, Trash2, Wifi, WifiOff, Pencil, Shield, Heart, MessageCircle } from 'lucide-react';
import { getCommunitySupport } from '../../services/contentService';
import { calculatePregnancyWeek } from '../../utils/dateUtils';
import { getTopicForWeekAndRole } from '../../constants/dailyTopics';

interface CommunityProps {
    user: UserProfile;
}

interface Thread {
    id: string;
    title: string;
    content: string;
    replies: ChatMessage[];
}

// Extended message type with ownership and delete flag
interface ExtendedChatMessage extends ChatMessage {
    senderId?: string;
    isDeleted?: boolean;
    isAnonymous?: boolean;
    editedAt?: number;
}

export const Community: React.FC<CommunityProps> = ({ user }) => {
    const currentWeek = calculatePregnancyWeek(user.dueDate);

    // WebSocket connection
    const wsRef = useRef<CommunityRoomConnection | null>(null);
    const userIdRef = useRef<string>(user.email || `user-${Date.now()}`);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
    const [activeUsers, setActiveUsers] = useState(0);

    // Loading states
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [loadingThread, setLoadingThread] = useState(true);

    // Main Feed State
    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Anonymous Mode State
    const [isAnonymous, setIsAnonymous] = useState(false);

    // Thread State
    const [activeThread, setActiveThread] = useState<Thread | null>(null);
    const [dailyThread, setDailyThread] = useState<Thread>({
        id: 'daily-1',
        title: 'Daily Discussion',
        content: "Loading...",
        replies: []
    });
    const [threadInput, setThreadInput] = useState('');

    // Poll Creation State
    const [showPollModal, setShowPollModal] = useState(false);
    const [pollQuestion, setPollQuestion] = useState('');
    const [pollOptions, setPollOptions] = useState(['', '']);

    // Poll Peek State (See results without voting)
    const [peekedPolls, setPeekedPolls] = useState<Set<string>>(new Set());

    // Edit Message State
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editText, setEditText] = useState('');

    // Info Modal State
    const [showInfoModal, setShowInfoModal] = useState(false);

    // WebSocket connection setup
    useEffect(() => {
        // Use stable user ID from ref
        const userId = userIdRef.current;
        const displayName = isAnonymous ? 'Anonymous' : user.name;

        wsRef.current = createCommunityRoomConnection(
            currentWeek,
            userId,
            displayName,
            {
                onMessage: (message) => {
                    // Add new message from WebSocket
                    setMessages(prev => {
                        // Avoid duplicates
                        if (prev.some(m => m.id === message.id)) return prev;
                        return [...prev, { ...message, senderId: (message as any).senderId }];
                    });
                },
                onMessagesSync: (syncedMessages) => {
                    // Replace with synced messages (on connect/reconnect)
                    setMessages(syncedMessages.map(m => ({ ...m, senderId: (m as any).senderId })));
                    setLoadingMessages(false);
                },
                onPollUpdate: (messageId, poll) => {
                    setMessages(prev => prev.map(msg =>
                        msg.id === messageId ? { ...msg, poll } : msg
                    ));
                },
                onMessageDelete: (messageId) => {
                    // Handle delete from server (other clients or this client's persisted delete)
                    setMessages(prev => prev.map(msg =>
                        msg.id === messageId ? { ...msg, isDeleted: true, text: 'Message deleted' } : msg
                    ));
                },
                onMessageEdit: (messageId, newText, editedAt) => {
                    // Handle edit from server
                    setMessages(prev => prev.map(msg =>
                        msg.id === messageId ? { ...msg, text: newText, editedAt } : msg
                    ));
                },
                onUserCountChange: (count) => {
                    setActiveUsers(count);
                },
                onStatusChange: (status) => {
                    setConnectionStatus(status);
                    if (status === 'connected') {
                        setLoadingMessages(false);
                    }
                }
            }
        );

        wsRef.current.connect();

        return () => {
            wsRef.current?.disconnect();
        };
    }, [currentWeek, user.email, user.name]);

    // Load thread from D1 on mount (threads use REST API, separated by role)
    useEffect(() => {
        const loadThread = async () => {
            try {
                // Fetch role-specific thread (partners and pregnant users have separate discussions)
                const data = await chatApi.getThread(currentWeek, user.role || 'pregnant');
                setDailyThread({
                    id: data.id,
                    title: data.title,
                    content: data.content, // API returns role-specific content
                    replies: data.replies
                });
            } catch (error) {
                console.error('Failed to load thread:', error);
                // Fallback to local topic generation if API fails
                const roleSpecificTopic = getTopicForWeekAndRole(currentWeek, user.role || 'pregnant');
                setDailyThread({
                    id: `daily-${currentWeek}`,
                    title: 'Daily Discussion',
                    content: roleSpecificTopic,
                    replies: []
                });
            } finally {
                setLoadingThread(false);
            }
        };
        loadThread();
    }, [currentWeek, user.role]);

    const scrollToBottom = () => {
        if (!activeThread) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, showPollModal]);

    const handleSend = () => {
        if (!input.trim() || !wsRef.current) return;

        const messageText = input.trim();
        const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Optimistically add message to local state
        const optimisticMessage: ExtendedChatMessage = {
            id: messageId,
            sender: 'user',
            senderName: isAnonymous ? 'Anonymous' : user.name,
            text: messageText,
            timestamp: Date.now(),
            senderId: userIdRef.current,
            isAnonymous: isAnonymous
        };

        setMessages(prev => [...prev, optimisticMessage]);

        // Send via WebSocket for real-time broadcast, passing anonymous state
        wsRef.current.sendMessage(messageText, isAnonymous);
        setInput('');
    };

    // Delete own message (soft delete via WebSocket for persistence)
    const handleDeleteMessage = (messageId: string) => {
        if (!window.confirm('Delete this message?')) return;

        // Optimistic update
        setMessages(prev => prev.map(msg =>
            msg.id === messageId ? { ...msg, isDeleted: true, text: 'Message deleted' } : msg
        ));
        // Persist via WebSocket
        wsRef.current?.deleteMessage(messageId);
    };

    // Start editing a message
    const startEditMessage = (messageId: string, currentText: string) => {
        setEditingMessageId(messageId);
        setEditText(currentText);
    };

    // Submit edited message
    const handleEditMessage = () => {
        if (!editingMessageId || !editText.trim() || !wsRef.current) return;

        // Optimistic update
        setMessages(prev => prev.map(msg =>
            msg.id === editingMessageId ? { ...msg, text: editText.trim(), editedAt: Date.now() } : msg
        ));
        // Persist via WebSocket
        wsRef.current.editMessage(editingMessageId, editText.trim());
        setEditingMessageId(null);
        setEditText('');
    };

    // Cancel editing
    const cancelEdit = () => {
        setEditingMessageId(null);
        setEditText('');
    };

    const handleThreadReply = async () => {
        if (!threadInput.trim()) return;

        const newReply: ChatMessage = {
            id: `tr-${Date.now()}`,
            sender: 'user',
            senderName: isAnonymous ? 'Anonymous' : user.name,
            text: threadInput,
            timestamp: Date.now()
        };

        const updatedThread = {
            ...dailyThread,
            replies: [...dailyThread.replies, newReply]
        };

        setDailyThread(updatedThread);
        setActiveThread(updatedThread);
        setThreadInput('');

        // Persist to D1
        try {
            await chatApi.postReply({
                threadId: dailyThread.id,
                text: threadInput,
                senderName: user.name,
                isAnonymous
            });
        } catch (error) {
            console.error('Failed to post reply:', error);
        }
    };

    const handleCreatePoll = () => {
        const validOptions = pollOptions.filter(o => o.trim());
        if (!pollQuestion.trim() || validOptions.length < 2 || !wsRef.current) return;

        const pollId = `poll-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Create optimistic poll message
        const optimisticPoll: ExtendedChatMessage = {
            id: pollId,
            sender: 'user',
            senderName: isAnonymous ? 'Anonymous' : user.name,
            text: `Poll: ${pollQuestion.trim()}`,
            timestamp: Date.now(),
            senderId: userIdRef.current,
            isAnonymous: isAnonymous,
            poll: {
                question: pollQuestion.trim(),
                options: validOptions.map((text, i) => ({
                    id: `opt-${i}-${Date.now()}`,
                    text,
                    votes: 0
                })),
                totalVotes: 0
            }
        };

        setMessages(prev => [...prev, optimisticPoll]);

        // Send poll via WebSocket for real-time broadcast, respecting anonymous mode
        wsRef.current.createPoll(pollQuestion.trim(), validOptions, isAnonymous);

        setShowPollModal(false);
        setPollQuestion('');
        setPollOptions(['', '']);
    };

    const updatePollOption = (index: number, val: string) => {
        const newOpts = [...pollOptions];
        newOpts[index] = val;
        setPollOptions(newOpts);
    };

    const addPollOption = () => {
        if (pollOptions.length < 5) {
            setPollOptions([...pollOptions, '']);
        }
    };

    const handleVote = (msgId: string, optionId: string) => {
        if (!wsRef.current) return;

        // Send vote via WebSocket for real-time update
        wsRef.current.votePoll(msgId, optionId);

        // Optimistic update
        setMessages(prev => prev.map(msg => {
            if (msg.id !== msgId || !msg.poll || msg.poll.userVotedOptionId) return msg;

            return {
                ...msg,
                poll: {
                    ...msg.poll,
                    totalVotes: msg.poll.totalVotes + 1,
                    userVotedOptionId: optionId,
                    options: msg.poll.options.map(opt =>
                        opt.id === optionId ? { ...opt, votes: opt.votes + 1 } : opt
                    )
                }
            };
        }));
    };

    const handlePeek = (msgId: string) => {
        setPeekedPolls(prev => {
            const next = new Set(prev);
            if (next.has(msgId)) next.delete(msgId);
            else next.add(msgId);
            return next;
        });
    };

    // Delete a poll message
    const handleDeletePoll = (messageId: string) => {
        if (!window.confirm('Delete this poll?')) return;

        // Optimistic update
        setMessages(prev => prev.map(msg =>
            msg.id === messageId ? { ...msg, isDeleted: true, text: 'Poll deleted' } : msg
        ));
        // Persist via WebSocket
        wsRef.current?.deleteMessage(messageId);
    };

    const renderPoll = (msg: ChatMessage, isMe: boolean = false, isAnon: boolean = false) => {
        if (!msg.poll) return null;
        const { poll } = msg;
        const isPeeked = peekedPolls.has(msg.id);
        const hasVoted = !!poll.userVotedOptionId;
        const showResults = hasVoted || isPeeked;

        // Determine poll card styling based on ownership and anonymous status
        const pollBgClass = isMe
            ? isAnon
                ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200'
                : 'bg-gradient-to-br from-stone-50 to-stone-100 border-stone-300'
            : 'bg-white border-stone-200';

        const pollAccentClass = isMe
            ? isAnon
                ? 'text-indigo-500'
                : 'text-stone-600'
            : 'text-emerald-500';

        const pollLabelClass = isMe
            ? isAnon
                ? 'text-indigo-600'
                : 'text-stone-600'
            : 'text-emerald-600';

        return (
            <div className="relative group">
                <div className={`${pollBgClass} border rounded-2xl p-4 mt-1 w-full max-w-[280px] shadow-sm ${isMe ? 'ml-auto' : ''}`}>
                    <div className="flex items-center gap-2 mb-3">
                        <BarChart2 size={16} className={pollAccentClass} />
                        <span className={`text-xs font-bold ${pollLabelClass} uppercase tracking-wider flex-1`}>
                            {isMe ? (isAnon ? 'Your Anonymous Poll' : 'Your Poll') : 'Community Poll'}
                        </span>
                        {/* Delete button for own polls */}
                        {isMe && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleDeletePoll(msg.id); }}
                                className="p-1.5 text-stone-300 hover:text-red-500 active:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-all touch-manipulation opacity-60 hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                title="Delete poll"
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                    <h3 className="font-bold text-stone-900 mb-4">{poll.question}</h3>

                    <div className="space-y-2">
                        {showResults ? (
                            poll.options.map(opt => {
                                const percentage = poll.totalVotes > 0 ? Math.round((opt.votes / poll.totalVotes) * 100) : 0;
                                const isVoted = poll.userVotedOptionId === opt.id;

                                return (
                                    <div key={opt.id} className="relative">
                                        <div className="relative h-10 w-full bg-stone-50 rounded-lg overflow-hidden border border-stone-100">
                                            {/* Background Bar */}
                                            <div
                                                className={`absolute top-0 left-0 h-full transition-all duration-500 ${isVoted ? 'bg-emerald-100' : 'bg-stone-200/50'}`}
                                                style={{ width: `${percentage}%` }}
                                            />

                                            {/* Content */}
                                            <div className="absolute inset-0 flex items-center justify-between px-3">
                                                <span className={`text-sm font-medium z-10 ${isVoted ? 'text-emerald-900' : 'text-stone-700'}`}>
                                                    {opt.text}
                                                </span>
                                                <div className="flex items-center gap-2 z-10">
                                                    {isVoted && <Check size={14} className="text-emerald-600" />}
                                                    <span className="text-xs font-bold text-stone-500">{percentage}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            poll.options.map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => handleVote(msg.id, opt.id)}
                                    className="w-full text-left p-3 rounded-xl border border-stone-200 hover:bg-stone-50 hover:border-emerald-200 transition-all text-sm font-medium text-stone-700 active:scale-[0.98]"
                                >
                                    {opt.text}
                                </button>
                            ))
                        )}
                    </div>

                    <div className="mt-3 flex items-center justify-between pt-2 border-t border-stone-50/50">
                        <div className="text-xs text-stone-400 font-medium">
                            {poll.totalVotes} votes
                        </div>
                        {!hasVoted && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handlePeek(msg.id); }}
                                className="text-xs font-bold text-stone-400 hover:text-stone-600 transition-colors flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-stone-50"
                            >
                                {isPeeked ? (
                                    'Back to Vote'
                                ) : (
                                    <><Eye size={14} /> Results</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // -- THREAD VIEW --
    if (activeThread) {
        return (
            <div className="flex flex-col h-full bg-stone-50 animate-slide-in-right">
                {/* Thread Header */}
                <div className="bg-white px-4 py-3 border-b border-stone-200 sticky top-0 z-10 shadow-sm flex items-center gap-3">
                    <button
                        onClick={() => setActiveThread(null)}
                        className="p-2 bg-stone-100 rounded-full text-stone-500 hover:bg-stone-200 transition-colors active:scale-95"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <div>
                        <h2 className="font-bold text-stone-900 leading-none">{activeThread.title}</h2>
                        <p className="text-xs text-stone-500 font-medium mt-1">Week {currentWeek} Cohort</p>
                    </div>
                </div>

                {/* Thread Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {/* Hero Card */}
                    <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-2xl shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <MessageSquareQuote className="w-5 h-5 text-indigo-500" />
                            <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Original Post</span>
                        </div>
                        <h3 className="text-lg font-bold text-indigo-900 leading-snug">
                            "{activeThread.content}"
                        </h3>
                    </div>

                    <div className="relative">
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-stone-200" />

                        <div className="space-y-6">
                            {activeThread.replies.map((reply) => {
                                const isMe = reply.sender === 'user';
                                return (
                                    <div key={reply.id} className="relative pl-8">
                                        {/* Connector Line */}
                                        <div className="absolute left-4 top-3 w-3 h-3 border-b-2 border-l-2 border-stone-200 rounded-bl-lg" />

                                        <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                            {!isMe && (
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-bold text-stone-500">{reply.senderName}</span>
                                                    <span className="text-[10px] text-stone-300">• {new Date(reply.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            )}
                                            <div className={`group relative flex items-center gap-1 w-full ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                                <div className={`min-w-0 max-w-[85%] px-4 py-3 rounded-2xl text-sm shadow-sm ${isMe
                                                    ? 'bg-stone-800 text-white rounded-br-sm'
                                                    : 'bg-white text-stone-800 border border-stone-200 rounded-tl-sm'
                                                    }`}>
                                                    <span className="break-words whitespace-pre-wrap">{reply.text}</span>
                                                </div>
                                                {/* Delete button for own replies - visible on mobile, hover on desktop */}
                                                {isMe && (
                                                    <button
                                                        onClick={async () => {
                                                            if (!window.confirm('Delete this reply?')) return;

                                                            try {
                                                                await chatApi.deleteReply(reply.id);
                                                                // Remove from local state
                                                                setDailyThread(prev => ({
                                                                    ...prev,
                                                                    replies: prev.replies.filter(r => r.id !== reply.id)
                                                                }));
                                                                if (activeThread) {
                                                                    setActiveThread(prev => prev ? ({
                                                                        ...prev,
                                                                        replies: prev.replies.filter(r => r.id !== reply.id)
                                                                    }) : null);
                                                                }
                                                            } catch (error) {
                                                                console.error('Failed to delete reply:', error);
                                                            }
                                                        }}
                                                        className="p-2 text-stone-400 hover:text-red-500 active:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-full transition-all touch-manipulation opacity-60 sm:opacity-0 sm:group-hover:opacity-100"
                                                        title="Delete reply"
                                                        aria-label="Delete reply"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            {isMe && (
                                                <span className="text-[9px] text-stone-300 mt-1 mx-1">
                                                    {new Date(reply.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Thread Input */}
                <div className="p-3 bg-white border-t border-stone-100">
                    <form
                        onSubmit={(e) => { e.preventDefault(); handleThreadReply(); }}
                        className="flex items-center gap-2 bg-stone-50 p-1.5 rounded-2xl border border-stone-200 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all"
                    >
                        <input
                            type="text"
                            value={threadInput}
                            onChange={(e) => setThreadInput(e.target.value)}
                            placeholder="Reply to thread..."
                            className="flex-1 bg-transparent border-0 px-3 py-2 text-stone-900 placeholder-stone-400 focus:ring-0 outline-none text-sm"
                        />
                        <button
                            type="submit"
                            disabled={!threadInput.trim()}
                            className="p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // -- MAIN FEED --
    return (
        <div className="flex flex-col h-full bg-stone-50 relative">
            {/* Info Modal Overlay */}
            {showInfoModal && (
                <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center animate-fade-in">
                    <div className="bg-white w-full sm:w-11/12 max-w-md rounded-t-3xl sm:rounded-3xl p-6 animate-slide-up shadow-2xl max-h-[85vh] overflow-y-auto no-scrollbar">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">
                                <Users className="text-emerald-500" /> Community Guidelines
                            </h3>
                            <button onClick={() => setShowInfoModal(false)} className="p-2 bg-stone-100 rounded-full text-stone-500 hover:bg-stone-200 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-5">
                            {/* About Section */}
                            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4">
                                <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                                    <Heart size={16} className="text-emerald-600" /> What is this space?
                                </h4>
                                <p className="text-sm text-emerald-800 leading-relaxed">
                                    Connect with others at the same pregnancy week as you. Share experiences, ask questions, and support each other through this journey.
                                </p>
                            </div>

                            {/* Features */}
                            <div className="space-y-3">
                                <h4 className="font-bold text-stone-700 text-sm uppercase tracking-wider">Features</h4>

                                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                                    <Ghost className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-stone-800 text-sm">Anonymous Mode</p>
                                        <p className="text-xs text-stone-500 mt-0.5">
                                            Tap the ghost icon to post anonymously. Perfect for sensitive topics.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                                    <BarChart2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-stone-800 text-sm">Polls</p>
                                        <p className="text-xs text-stone-500 mt-0.5">
                                            Create polls to get community opinions. Peek at results or vote.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                                    <MessageSquareQuote className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold text-stone-800 text-sm">Daily Discussion</p>
                                        <p className="text-xs text-stone-500 mt-0.5">
                                            Each day has a new topic tailored to your week. Tap to join.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Guidelines */}
                            <div className="space-y-3">
                                <h4 className="font-bold text-stone-700 text-sm uppercase tracking-wider">Guidelines</h4>

                                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                                    <ul className="space-y-2 text-sm text-amber-900">
                                        <li className="flex items-start gap-2">
                                            <Shield size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                            <span>Be kind and supportive to others</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Shield size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                            <span>No medical advice - consult your provider</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Shield size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                            <span>Respect privacy - don't share personal info</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Shield size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                                            <span>Report concerning content to admin</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            {/* Privacy Note */}
                            <div className="text-center pt-2">
                                <p className="text-xs text-stone-400">
                                    Messages are visible to your week {currentWeek} cohort only.
                                    <br />Your real name is hidden when posting anonymously.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowInfoModal(false)}
                            className="w-full py-3 bg-stone-900 text-white rounded-xl font-bold mt-6 active:scale-[0.98] transition-transform"
                        >
                            Got it!
                        </button>
                    </div>
                </div>
            )}

            {/* Poll Creator Overlay */}
            {showPollModal && (
                <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center animate-fade-in">
                    <div className="bg-white w-full sm:w-11/12 max-w-sm rounded-t-3xl sm:rounded-3xl p-6 animate-slide-up shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2">
                                <BarChart2 className="text-emerald-500" /> New Poll
                            </h3>
                            <button onClick={() => setShowPollModal(false)} className="p-2 bg-stone-100 rounded-full text-stone-500">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4 max-h-[60vh] overflow-y-auto no-scrollbar pb-4">
                            <div>
                                <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Question</label>
                                <input
                                    value={pollQuestion}
                                    onChange={(e) => setPollQuestion(e.target.value)}
                                    placeholder="Ask the group..."
                                    className="w-full p-3 bg-stone-50 rounded-xl border-2 border-stone-100 focus:border-emerald-500 outline-none transition-colors font-medium"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="block text-xs font-bold text-stone-400 uppercase mb-1">Options</label>
                                {pollOptions.map((opt, i) => (
                                    <input
                                        key={i}
                                        value={opt}
                                        onChange={(e) => updatePollOption(i, e.target.value)}
                                        placeholder={`Option ${i + 1}`}
                                        className="w-full p-3 bg-stone-50 rounded-xl border border-stone-100 focus:border-emerald-300 outline-none text-sm"
                                    />
                                ))}
                                {pollOptions.length < 4 && (
                                    <button
                                        onClick={addPollOption}
                                        className="text-xs font-bold text-emerald-600 flex items-center gap-1 p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                                    >
                                        <Plus size={14} /> Add Option
                                    </button>
                                )}
                            </div>

                            <button
                                onClick={handleCreatePoll}
                                disabled={!pollQuestion || pollOptions.filter(o => o.trim()).length < 2}
                                className="w-full py-4 bg-stone-900 text-white rounded-xl font-bold mt-4 disabled:opacity-50"
                            >
                                Post Poll
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Context Header */}
            <div className="bg-white px-4 py-3 border-b border-stone-200 sticky top-0 z-10 shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-emerald-100 p-2 rounded-xl relative">
                            <Users className="w-5 h-5 text-emerald-700" />
                            {/* Connection indicator */}
                            <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${connectionStatus === 'connected' ? 'bg-emerald-500' :
                                connectionStatus === 'connecting' ? 'bg-amber-400 animate-pulse' :
                                    'bg-red-400'
                                }`} />
                        </div>
                        <div>
                            <h2 className="font-bold text-stone-900 leading-none">Week {currentWeek} Cohort</h2>
                            <p className="text-[10px] text-stone-500 font-medium uppercase tracking-wide mt-1">
                                {activeUsers > 0 ? `${activeUsers} Online` : 'Connecting...'} • Similar Due Dates
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {connectionStatus !== 'connected' && (
                            <WifiOff size={16} className="text-amber-500" />
                        )}
                        <button
                            onClick={() => setShowInfoModal(true)}
                            className="p-1.5 hover:bg-stone-100 rounded-lg transition-colors active:scale-95"
                            aria-label="Community info"
                        >
                            <Info className="w-5 h-5 text-stone-400 hover:text-stone-600" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto px-4 relative">

                {/* Sticky Daily Topic Card */}
                <div className="sticky top-0 z-10 bg-stone-50/95 backdrop-blur-sm pt-4 pb-2 -mx-4 px-4 border-b border-stone-100/50">
                    <div
                        onClick={() => setActiveThread(dailyThread)}
                        className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl flex items-start gap-3 shadow-sm cursor-pointer hover:bg-indigo-100/50 transition-colors group active:scale-[0.99]"
                    >
                        <MessageSquareQuote className="w-6 h-6 text-indigo-500 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                            <span className="text-xs font-bold text-indigo-400 uppercase tracking-wider">Daily Discussion</span>
                            <p className="text-indigo-900 font-medium text-sm mt-1 leading-snug">
                                "{dailyThread.content}"
                            </p>
                            <div className="flex items-center gap-4 mt-3">
                                <button className="text-xs text-indigo-600 font-bold hover:underline flex items-center gap-1 bg-white/50 px-2 py-1 rounded-lg">
                                    <CornerDownRight size={12} /> Reply to thread
                                </button>
                                {dailyThread.replies.length > 0 && (
                                    <span className="text-[10px] font-bold text-indigo-400">
                                        {dailyThread.replies.length} replies
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="text-center text-xs text-stone-400 my-4">
                    <span className="bg-stone-100 px-3 py-1 rounded-full">Today</span>
                </div>

                <div className="space-y-4 pb-4">
                    {messages.filter(msg => !msg.isDeleted).map((msg) => {
                        // Check ownership by comparing senderId with our userId
                        // The senderId is always set by the server based on the WebSocket connection
                        const isMe = msg.senderId === userIdRef.current;
                        // Check the isAnonymous flag for anonymous detection
                        const isAnon = msg.isAnonymous === true;
                        return (
                            <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} animate-fade-in`}>
                                {/* Sender label */}
                                {!isMe && (
                                    <span className="text-[10px] font-bold text-stone-400 mb-1 ml-1">
                                        {msg.senderName}
                                    </span>
                                )}
                                {isMe && isAnon && (
                                    <span className="text-[10px] font-bold text-indigo-400 mb-1 mr-1 flex items-center gap-1">
                                        <Ghost size={10} /> You (Anonymous)
                                    </span>
                                )}
                                {isMe && !isAnon && (
                                    <span className="text-[10px] font-bold text-stone-500 mb-1 mr-1">You</span>
                                )}

                                {msg.poll ? (
                                    renderPoll(msg, isMe, isAnon)
                                ) : editingMessageId === msg.id ? (
                                    /* Edit Mode */
                                    <div className="w-full max-w-[85%] sm:max-w-[80%]">
                                        <div className="flex items-center gap-2 bg-white border-2 border-indigo-300 rounded-2xl p-2 shadow-sm">
                                            <input
                                                type="text"
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleEditMessage();
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                className="flex-1 bg-transparent border-0 px-2 py-1 text-stone-900 focus:ring-0 outline-none text-sm"
                                                autoFocus
                                            />
                                            <button
                                                onClick={handleEditMessage}
                                                className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                                            >
                                                <Check size={14} />
                                            </button>
                                            <button
                                                onClick={cancelEdit}
                                                className="p-1.5 bg-stone-200 text-stone-600 rounded-lg hover:bg-stone-300 transition-colors"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={`group relative flex items-center gap-1 w-full ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                        {/* Message bubble */}
                                        <div className={`min-w-0 max-w-[85%] sm:max-w-[75%] px-4 py-3 rounded-2xl shadow-sm transition-all ${isMe
                                            ? isAnon
                                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-br-sm'
                                                : 'bg-gradient-to-br from-stone-700 to-stone-800 text-white rounded-br-sm'
                                            : 'bg-white text-stone-800 border border-stone-100 rounded-bl-sm'
                                            }`}>
                                            <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.text}</p>
                                            {(msg as any).editedAt && (
                                                <span className={`text-[9px] mt-1 block ${isMe ? 'text-white/60' : 'text-stone-400'}`}>
                                                    (edited)
                                                </span>
                                            )}
                                        </div>

                                        {/* Edit & Delete buttons - always visible on mobile, hover on desktop */}
                                        {isMe && (
                                            <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => startEditMessage(msg.id, msg.text)}
                                                    className="p-2 text-stone-400 hover:text-indigo-500 active:text-indigo-600 hover:bg-indigo-50 active:bg-indigo-100 rounded-full transition-all touch-manipulation"
                                                    title="Edit message"
                                                    aria-label="Edit message"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteMessage(msg.id)}
                                                    className="p-2 text-stone-400 hover:text-red-500 active:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-full transition-all touch-manipulation"
                                                    title="Delete message"
                                                    aria-label="Delete message"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <span className="text-[9px] text-stone-300 mt-1 mx-1">
                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                        );
                    })}

                    {isTyping && (
                        <div className="flex justify-start">
                            <div className="bg-white border border-stone-100 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm flex items-center space-x-1">
                                <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-1.5 h-1.5 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-stone-100">
                <div className={`flex items-center gap-2 bg-stone-50 p-1.5 rounded-2xl border transition-all ${isAnonymous ? 'border-stone-400 focus-within:ring-2 focus-within:ring-stone-200' : 'border-stone-200 focus-within:ring-2 focus-within:ring-emerald-500/20 focus-within:border-emerald-500'}`}>
                    <button
                        onClick={() => setShowPollModal(true)}
                        className="p-2 text-stone-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors"
                    >
                        <BarChart2 className="w-5 h-5" />
                    </button>

                    <button
                        onClick={() => setIsAnonymous(!isAnonymous)}
                        className={`p-2 rounded-xl transition-colors ${isAnonymous ? 'bg-stone-800 text-white' : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100'}`}
                    >
                        <Ghost className="w-5 h-5" />
                    </button>

                    <form
                        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                        className="flex-1 flex items-center gap-2"
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={isAnonymous ? "Share anonymously..." : "Share with the group..."}
                            className="flex-1 bg-transparent border-0 px-2 py-2 text-stone-900 placeholder-stone-400 focus:ring-0 outline-none text-sm"
                        />
                        <button
                            type="submit"
                            disabled={!input.trim() || isTyping}
                            className={`p-2 text-white rounded-xl disabled:opacity-50 transition-colors shadow-sm ${isAnonymous ? 'bg-stone-800 hover:bg-stone-900' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};
